import React, { useEffect, useMemo, useRef } from "react";
import styled from "styled-components/macro";
import { useLocation } from "react-router-dom";

import QuestionDescription from "../../../components/QuestionDescription";
import ReviewText from "../../../components/ReviewText";
import ScrollToTop from "../../../components/utils/ScrollToTop";
import { formItems } from "../../../shared/informationRequest/informationRequestConfiguration";
import { estateItemRowFor } from "../../../shared/probateEstate/estateItemRows";
import { sanitiseHtml } from "../../../utils/sanitiseHtml";
import { Button } from "../../../probate/components/ui/button";
import { Tray, TrayActions, TrayHeader } from "../../../probate/components/tray";
import useDynamicFormDoable from "../../../hooks/useDynamicFormDoable";
import CompletedAnswers, { completedResponses } from "../dynamicForm/CompletedAnswers";
import DynamicFormLoader, { formatSavedAt } from "../dynamicForm/DynamicFormLoader";
import FormQuestions from "../dynamicForm/FormQuestions";
import ReviewAnswers from "../dynamicForm/ReviewAnswers";
import { useCaseFromPath } from "../utils/hooks";
import ButtonLink from "./ButtonLink";
import EstateItemRow from "./EstateItemRow";
import ProbatePage from "./ProbatePage";
import { SheetBody } from "./Sheet";

// Questions 24px apart, in place of each legacy question's own bottom margin. A hidden question
// renders an empty wrapper, which would add a gap.
export const Questions = styled.div`
  --question-margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: var(--juno-space-6);

  > :empty {
    display: none;
  }
`;

// 24px from the tray's header and from its button
const TaskContent = styled(Questions)`
  padding: var(--juno-space-4) 0;
`;

// A longer form follows the description, at its 40px inset, with the button after the questions
const Form = styled(Questions)`
  padding: 0 var(--juno-space-6) var(--juno-space-4);

  > [data-slot="tray-actions"] {
    padding: 0;
  }
`;

// A done request's answers step back, as the design's done state does. 0.64 is --muted-foreground's
// alpha, so answers in the ink colour keep its 4.75:1 on white.
const DoneAnswers = styled(Questions)`
  opacity: 0.64;
`;

export const TaskError = styled.p`
  margin: 0;
  padding-top: var(--tray-actions-padding-top, 0);
  color: var(--destructive);
`;

const Description = ({ doable }) =>
  doable.description ? (
    <QuestionDescription dangerouslySetInnerHTML={{ __html: sanitiseHtml(doable.description) }} />
  ) : null;

// A request with one question, such as an upload, is a task in an outlined tray. A longer form
// sits in the sheet, as the design's longer forms do.
const Task = ({ single, error, errorRef, action, children }) => {
  const errorMessage = error && (
    <TaskError ref={errorRef} role="alert">
      {error}
    </TaskError>
  );
  const actions = action && <TrayActions>{action}</TrayActions>;

  return single ? (
    <Tray variant="outlined">
      <TrayHeader title="Task" />
      <TaskContent>{children}</TaskContent>
      {errorMessage}
      {actions}
    </Tray>
  ) : (
    <Form>
      {children}
      {errorMessage}
      {actions}
    </Form>
  );
};

const singleQuestion = (configuration) => formItems(configuration).itemIdentifiers.length === 1;

const ProbateForm = ({
  doable,
  configuration,
  initialDocumentUploads,
  postal2FACode,
  submissionEnabled,
  savedAt,
  saveDraft,
  cancelPendingSave,
}) => {
  // Not the caseRef prop: this finds the case holding the doable when the page opens from an email
  const kase = useCaseFromPath();
  const caseRef = kase?.case_reference;
  const back = useLocation().state?.back;
  const form = useDynamicFormDoable({
    doable,
    configuration,
    initialDocumentUploads,
    postal2FACode,
    saveDraft,
    cancelPendingSave,
  });
  const {
    reviewing,
    reviewResponses,
    submitted,
    submissionError,
    isDuplicateSubmission,
    uploadInProgress,
    submitResponses,
    saveResponses,
  } = form;
  const errorRef = useRef(null);
  const estateItemRow = useMemo(() => estateItemRowFor(configuration), [configuration]);
  const renderEstateItems = (name) =>
    kase &&
    name === estateItemRow?.gateField &&
    form.responses[name] === "yes" && (
      <EstateItemRow kase={kase} row={estateItemRow} doableId={doable.id} canAdd={submissionEnabled} back={back} />
    );

  // useDynamicFormDoable scrolls to the top on an error; this error sits by the button instead
  useEffect(() => {
    if (submissionError && !isDuplicateSubmission) {
      errorRef.current?.scrollIntoView?.({ block: "center" });
    }
  }, [submissionError, isDuplicateSubmission]);

  if (submitted || isDuplicateSubmission) {
    return (
      <ProbatePage caseRef={caseRef} title={doable.title} done={submitted}>
        <SheetBody>
          <p>
            {submitted
              ? "We’ve got your answers. We’ll email you if we need anything else."
              : "You’ve already sent us your answers."}
          </p>
          {back ? (
            <ButtonLink to={back.to}>Back to the list</ButtonLink>
          ) : (
            <ButtonLink to={`/dashboard/${caseRef}`}>Back to your dashboard</ButtonLink>
          )}
        </SheetBody>
      </ProbatePage>
    );
  }

  const error = submissionError && "We couldn’t save your answers. Try again, or send us a message using web chat.";
  const savedAtText = savedAt && formatSavedAt(savedAt);

  return (
    <ProbatePage caseRef={caseRef} title={doable.title}>
      {!reviewing && <ScrollToTop />}
      <SheetBody>
        <Description doable={doable} />
        {!reviewing && savedAtText && <p>Your answers save automatically — last saved {savedAtText}.</p>}
      </SheetBody>
      {reviewing ? (
        <Task
          single={singleQuestion(configuration)}
          error={error}
          errorRef={errorRef}
          action={
            <Button variant="secondary" onClick={submitResponses}>
              Submit your answers
            </Button>
          }
        >
          <ReviewText>
            We’ve listed your answers below — if anything doesn’t look right, you can change your answers before you
            submit.
          </ReviewText>
          <ReviewAnswers configuration={configuration} form={form} />
        </Task>
      ) : (
        <Task
          single={singleQuestion(configuration)}
          error={error}
          errorRef={errorRef}
          action={
            <Button
              variant={uploadInProgress ? "default" : "secondary"}
              disabled={uploadInProgress}
              onClick={configuration.check_before_submit ? reviewResponses : saveResponses}
            >
              {configuration.check_before_submit ? "Continue" : "Save your answers"}
            </Button>
          }
        >
          <FormQuestions
            configuration={configuration}
            form={form}
            caseRef={caseRef}
            doableId={doable.id}
            renderAfter={renderEstateItems}
          />
        </Task>
      )}
    </ProbatePage>
  );
};

const ProbateCompletedRequest = ({ doable }) => {
  const kase = useCaseFromPath();
  const caseRef = kase?.case_reference;
  const responses = useMemo(() => completedResponses(doable), [doable]);
  const answered = doable.submission && formItems(doable.configuration).itemIdentifiers.length > 0;
  // Add closes with the request. Adding from a done row comes later, as an unprompted submission.
  const estateItemRow = useMemo(() => estateItemRowFor(doable.configuration), [doable.configuration]);
  const listsEstateItems = kase && estateItemRow && responses[estateItemRow.gateField] === "yes";

  return (
    <ProbatePage caseRef={caseRef} title={doable.title} done>
      <SheetBody>
        <Description doable={doable} />
        {!answered && <p>We’ll be in touch if we have any questions.</p>}
      </SheetBody>
      {answered && (
        <Task single={singleQuestion(doable.configuration)}>
          <DoneAnswers>
            <CompletedAnswers doable={doable} responses={responses} />
          </DoneAnswers>
          {listsEstateItems && <EstateItemRow kase={kase} row={estateItemRow} doableId={doable.id} />}
        </Task>
      )}
    </ProbatePage>
  );
};

const ProbateDoable = (props) =>
  props.doable.status === "complete" ? (
    <ProbateCompletedRequest doable={props.doable} />
  ) : (
    <DynamicFormLoader {...props} Form={ProbateForm} />
  );

export default ProbateDoable;
